/**
 * Which number a KPI chart card leads with (DOP-609). Kept apart from the
 * card so the rule is testable without the chart library.
 */

import type { KpiMetricRow } from "../../types/kpi";

export function valueAt(metric: KpiMetricRow, period: string | undefined): number | null {
	if (!period) return null;
	return metric.cells[period]?.effective_value ?? null;
}

export interface Headline {
	metric: KpiMetricRow;
	period: string;
	value: number;
	previousPeriod: string | null;
	previous: number | null;
}

/**
 * The number the card leads with. A fixed, documented rule — the table
 * payload carries no "headline" role, so nothing better is knowable here:
 *
 * 1. The period is the LATEST one in which any plotted metric has a value —
 *    one period for the whole card, never a different month per metric.
 * 2. The metric is the LAST plotted one with a value in that period. Packs
 *    list a group's lines first and its total last ("Effective gross income"
 *    under the revenue lines), so on a well-formed group that is the total.
 * 3. The card names the metric beside the number whenever the group plots
 *    more than one, so a headline that is not the total says so.
 * 4. The comparison is that metric in the period immediately before; when it
 *    has no value there, no change is shown rather than one against an older
 *    period passed off as "last period".
 */
export function headlineOf(plotted: KpiMetricRow[], periods: string[]): Headline | null {
	for (let p = periods.length - 1; p >= 0; p -= 1) {
		for (let m = plotted.length - 1; m >= 0; m -= 1) {
			const metric = plotted[m];
			const value = valueAt(metric, periods[p]);
			if (value === null) continue;
			const previousPeriod = p > 0 ? periods[p - 1] : null;
			return {
				metric,
				period: periods[p],
				value,
				previousPeriod,
				previous: previousPeriod ? valueAt(metric, previousPeriod) : null,
			};
		}
	}
	return null;
}
